import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Animated, Easing, Dimensions } from 'react-native';
import { Text, Card, useTheme, Button, IconButton } from 'react-native-paper';
import type { MasteryState } from '@coffeeplexity/shared';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

export interface MilestoneCelebration {
  id: string;
  userId: string;
  milestoneType:
    | 'skill_mastery'
    | 'streak_achievement'
    | 'level_up'
    | 'journey_completion'
    | 'peer_recognition';
  title: string;
  description: string;
  personalizedMessage: string;
  achievementData: {
    skillName?: string;
    newLevel?: string;
    streakDays?: number;
    journeyName?: string;
    peerCount?: number;
    previousBest?: number;
    currentValue: number;
  };
  rewards?: MilestoneReward[];
  shareableContent?: ShareableContent;
  nextGoal?: string;
  celebrationLevel: 'small' | 'medium' | 'large' | 'epic';
  timestamp: string;
}

export interface MilestoneReward {
  id: string;
  type: 'badge' | 'points' | 'unlock' | 'discount' | 'feature_access';
  name: string;
  description: string;
  icon: string;
  value?: number;
}

export interface ShareableContent {
  text: string;
  imageUrl?: string;
  hashtags: string[];
  platforms: ('twitter' | 'facebook' | 'instagram' | 'linkedin')[];
}

export interface ProgressMilestoneCelebrationProps {
  milestone: MilestoneCelebration;
  onCelebrationComplete: () => void;
  onShare?: (platform: string, content: ShareableContent) => void;
  onViewRewards?: (rewards: MilestoneReward[]) => void;
  autoPlay?: boolean;
  style?: any;
}

export function ProgressMilestoneCelebration({
  milestone,
  onCelebrationComplete,
  onShare,
  onViewRewards,
  autoPlay = true,
  style,
}: ProgressMilestoneCelebrationProps) {
  const theme = useTheme();
  const [currentPhase, setCurrentPhase] = useState<
    'entrance' | 'celebration' | 'content' | 'rewards' | 'exit'
  >('entrance');
  const [showShareOptions, setShowShareOptions] = useState(false);

  // Animation values
  const scaleAnimation = useRef(new Animated.Value(0)).current;
  const rotationAnimation = useRef(new Animated.Value(0)).current;
  const confettiAnimation = useRef(new Animated.Value(0)).current;
  const pulseAnimation = useRef(new Animated.Value(1)).current;
  const slideAnimation = useRef(new Animated.Value(screenHeight)).current;
  const sparkleAnimations = useRef(
    Array.from({ length: 8 }, () => new Animated.Value(0))
  ).current;

  useEffect(() => {
    if (autoPlay) {
      startCelebrationSequence();
    }
  }, [autoPlay]);

  const startCelebrationSequence = () => {
    // Phase 1: Entrance
    Animated.parallel([
      Animated.timing(slideAnimation, {
        toValue: 0,
        duration: 600,
        easing: Easing.out(Easing.back(1.5)),
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnimation, {
        toValue: 1,
        duration: 800,
        easing: Easing.out(Easing.elastic(1.2)),
        useNativeDriver: true,
      }),
    ]).start(() => {
      setCurrentPhase('celebration');
      startCelebrationAnimations();
    });
  };

  const startCelebrationAnimations = () => {
    // Phase 2: Celebration effects
    const celebrationIntensity = getCelebrationIntensity();

    // Confetti animation
    Animated.timing(confettiAnimation, {
      toValue: 1,
      duration: 2000,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();

    // Rotation animation for epic celebrations
    if (milestone.celebrationLevel === 'epic') {
      Animated.timing(rotationAnimation, {
        toValue: 1,
        duration: 1000,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    }

    // Pulse animation
    const pulseLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnimation, {
          toValue: 1.1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnimation, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    );
    pulseLoop.start();

    // Sparkle animations
    sparkleAnimations.forEach((anim, index) => {
      Animated.loop(
        Animated.sequence([
          Animated.delay(index * 200),
          Animated.timing(anim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true,
          }),
          Animated.timing(anim, {
            toValue: 0,
            duration: 1000,
            useNativeDriver: true,
          }),
        ])
      ).start();
    });

    // Transition to content phase
    setTimeout(() => {
      setCurrentPhase('content');
      pulseLoop.stop();
    }, celebrationIntensity * 1000);
  };

  const getCelebrationIntensity = () => {
    switch (milestone.celebrationLevel) {
      case 'small':
        return 2;
      case 'medium':
        return 3;
      case 'large':
        return 4;
      case 'epic':
        return 6;
      default:
        return 3;
    }
  };

  const getMilestoneIcon = () => {
    switch (milestone.milestoneType) {
      case 'skill_mastery':
        return '🏆';
      case 'streak_achievement':
        return '🔥';
      case 'level_up':
        return '⭐';
      case 'journey_completion':
        return '🎯';
      case 'peer_recognition':
        return '👥';
      default:
        return '🎉';
    }
  };

  const getCelebrationEmojis = () => {
    switch (milestone.celebrationLevel) {
      case 'small':
        return ['🎉', '✨'];
      case 'medium':
        return ['🎉', '✨', '🌟'];
      case 'large':
        return ['🎉', '✨', '🌟', '🎊'];
      case 'epic':
        return ['🎉', '✨', '🌟', '🎊', '🏆', '💫'];
      default:
        return ['🎉', '✨'];
    }
  };

  const handleComplete = () => {
    setCurrentPhase('exit');

    Animated.parallel([
      Animated.timing(slideAnimation, {
        toValue: -screenHeight,
        duration: 500,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnimation, {
        toValue: 0,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onCelebrationComplete();
    });
  };

  const handleShare = (platform: string) => {
    if (onShare && milestone.shareableContent) {
      onShare(platform, milestone.shareableContent);
    }
    setShowShareOptions(false);
  };

  const celebrationEmojis = getCelebrationEmojis();
  const milestoneIcon = getMilestoneIcon();

  const rotationInterpolation = rotationAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  return (
    <Animated.View
      style={[
        styles.container,
        style,
        {
          transform: [
            { translateY: slideAnimation },
            { scale: scaleAnimation },
          ],
        },
      ]}
    >
      {/* Background Overlay */}
      <View style={styles.overlay} />

      {/* Confetti Effect */}
      {currentPhase === 'celebration' && (
        <View style={styles.confettiContainer}>
          {celebrationEmojis.map((emoji, index) => (
            <Animated.Text
              key={index}
              style={[
                styles.confettiEmoji,
                {
                  left: `${(index * 15) % 100}%`,
                  top: `${(index * 20) % 80}%`,
                  opacity: confettiAnimation,
                  transform: [
                    {
                      translateY: confettiAnimation.interpolate({
                        inputRange: [0, 1],
                        outputRange: [-50, 100],
                      }),
                    },
                    {
                      rotate: confettiAnimation.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['0deg', '360deg'],
                      }),
                    },
                  ],
                },
              ]}
            >
              {emoji}
            </Animated.Text>
          ))}
        </View>
      )}

      {/* Sparkles */}
      {sparkleAnimations.map((anim, index) => (
        <Animated.View
          key={index}
          style={[
            styles.sparkle,
            {
              left: `${(index * 12.5) % 100}%`,
              top: `${(index * 15) % 100}%`,
              opacity: anim,
              transform: [
                {
                  scale: anim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, 1.5],
                  }),
                },
              ],
            },
          ]}
        >
          <Text style={styles.sparkleText}>✨</Text>
        </Animated.View>
      ))}

      {/* Main Content Card */}
      <Animated.View
        style={[
          styles.cardContainer,
          {
            transform: [
              { scale: pulseAnimation },
              {
                rotate:
                  milestone.celebrationLevel === 'epic'
                    ? rotationInterpolation
                    : '0deg',
              },
            ],
          },
        ]}
      >
        <Card style={styles.card} mode='elevated'>
          <Card.Content style={styles.content}>
            {/* Close Button */}
            <IconButton
              icon='close'
              size={20}
              onPress={handleComplete}
              style={styles.closeButton}
            />

            {/* Milestone Icon */}
            <View style={styles.iconContainer}>
              <Text style={styles.milestoneIcon}>{milestoneIcon}</Text>
            </View>

            {/* Title */}
            <Text variant='headlineSmall' style={styles.title}>
              {milestone.title}
            </Text>

            {/* Description */}
            <Text variant='bodyLarge' style={styles.description}>
              {milestone.description}
            </Text>

            {/* Personalized Message */}
            <View style={styles.personalizedSection}>
              <Text variant='bodyMedium' style={styles.personalizedMessage}>
                {milestone.personalizedMessage}
              </Text>
            </View>

            {/* Achievement Data */}
            <View style={styles.achievementData}>
              {milestone.achievementData.skillName && (
                <View style={styles.dataItem}>
                  <Text variant='bodySmall' style={styles.dataLabel}>
                    Skill
                  </Text>
                  <Text variant='bodyMedium' style={styles.dataValue}>
                    {milestone.achievementData.skillName}
                  </Text>
                </View>
              )}

              {milestone.achievementData.newLevel && (
                <View style={styles.dataItem}>
                  <Text variant='bodySmall' style={styles.dataLabel}>
                    New Level
                  </Text>
                  <Text variant='bodyMedium' style={styles.dataValue}>
                    {milestone.achievementData.newLevel}
                  </Text>
                </View>
              )}

              {milestone.achievementData.streakDays && (
                <View style={styles.dataItem}>
                  <Text variant='bodySmall' style={styles.dataLabel}>
                    Streak
                  </Text>
                  <Text variant='bodyMedium' style={styles.dataValue}>
                    {milestone.achievementData.streakDays} days
                  </Text>
                </View>
              )}

              <View style={styles.dataItem}>
                <Text variant='bodySmall' style={styles.dataLabel}>
                  Achievement
                </Text>
                <Text variant='headlineMedium' style={styles.currentValue}>
                  {milestone.achievementData.currentValue}
                  {milestone.achievementData.currentValue < 1 ? '%' : ''}
                </Text>
              </View>
            </View>

            {/* Rewards */}
            {milestone.rewards && milestone.rewards.length > 0 && (
              <View style={styles.rewardsSection}>
                <Text variant='titleSmall' style={styles.rewardsTitle}>
                  Rewards Unlocked!
                </Text>
                <View style={styles.rewardsList}>
                  {milestone.rewards.slice(0, 3).map(reward => (
                    <View key={reward.id} style={styles.rewardItem}>
                      <Text style={styles.rewardIcon}>{reward.icon}</Text>
                      <Text variant='bodySmall' style={styles.rewardName}>
                        {reward.name}
                      </Text>
                    </View>
                  ))}
                </View>
                {milestone.rewards.length > 3 && onViewRewards && (
                  <Button
                    mode='text'
                    onPress={() => onViewRewards(milestone.rewards!)}
                    style={styles.viewAllRewards}
                  >
                    View All Rewards
                  </Button>
                )}
              </View>
            )}

            {/* Next Goal */}
            {milestone.nextGoal && (
              <View style={styles.nextGoalSection}>
                <Text variant='bodySmall' style={styles.nextGoalLabel}>
                  Next Goal:
                </Text>
                <Text variant='bodyMedium' style={styles.nextGoalText}>
                  {milestone.nextGoal}
                </Text>
              </View>
            )}

            {/* Action Buttons */}
            <View style={styles.actions}>
              {milestone.shareableContent && (
                <Button
                  mode='outlined'
                  onPress={() => setShowShareOptions(!showShareOptions)}
                  style={styles.actionButton}
                  icon='share'
                >
                  Share
                </Button>
              )}

              <Button
                mode='contained'
                onPress={handleComplete}
                style={styles.actionButton}
              >
                Continue
              </Button>
            </View>

            {/* Share Options */}
            {showShareOptions && milestone.shareableContent && (
              <View style={styles.shareOptions}>
                {milestone.shareableContent.platforms.map(platform => (
                  <Button
                    key={platform}
                    mode='text'
                    onPress={() => handleShare(platform)}
                    style={styles.shareButton}
                    icon={platform}
                  >
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </Button>
                ))}
              </View>
            )}
          </Card.Content>
        </Card>
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  confettiContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  confettiEmoji: {
    position: 'absolute',
    fontSize: 24,
  },
  sparkle: {
    position: 'absolute',
  },
  sparkleText: {
    fontSize: 16,
  },
  cardContainer: {
    width: screenWidth - 32,
    maxHeight: screenHeight * 0.8,
  },
  card: {
    elevation: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.4,
    shadowRadius: 12,
  },
  content: {
    padding: 24,
    alignItems: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 1,
  },
  iconContainer: {
    marginBottom: 16,
  },
  milestoneIcon: {
    fontSize: 64,
  },
  title: {
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
    color: '#1976D2',
  },
  description: {
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 24,
  },
  personalizedSection: {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
    width: '100%',
  },
  personalizedMessage: {
    textAlign: 'center',
    fontStyle: 'italic',
    color: '#4CAF50',
    lineHeight: 20,
  },
  achievementData: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    marginBottom: 20,
    width: '100%',
  },
  dataItem: {
    alignItems: 'center',
    minWidth: '30%',
    marginBottom: 12,
  },
  dataLabel: {
    opacity: 0.7,
    textTransform: 'uppercase',
    fontSize: 10,
    marginBottom: 4,
  },
  dataValue: {
    fontWeight: '600',
  },
  currentValue: {
    fontWeight: '700',
    color: '#4CAF50',
  },
  rewardsSection: {
    width: '100%',
    marginBottom: 20,
  },
  rewardsTitle: {
    textAlign: 'center',
    fontWeight: '600',
    marginBottom: 12,
    color: '#FF9800',
  },
  rewardsList: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    flexWrap: 'wrap',
  },
  rewardItem: {
    alignItems: 'center',
    minWidth: '30%',
    marginBottom: 8,
  },
  rewardIcon: {
    fontSize: 24,
    marginBottom: 4,
  },
  rewardName: {
    textAlign: 'center',
    fontSize: 10,
  },
  viewAllRewards: {
    alignSelf: 'center',
    marginTop: 8,
  },
  nextGoalSection: {
    backgroundColor: 'rgba(123, 31, 162, 0.1)',
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
    width: '100%',
  },
  nextGoalLabel: {
    fontWeight: '600',
    color: '#7B1FA2',
    marginBottom: 4,
  },
  nextGoalText: {
    color: '#7B1FA2',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },
  shareOptions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0, 0, 0, 0.1)',
  },
  shareButton: {
    flex: 1,
  },
});
